import React from 'react';
import { Spin } from 'antd';
import { CheckCircleOutlined, BulbOutlined } from '@ant-design/icons';
import { ResumeReview, Readiness } from '../../../types';
import ReadinessBanner from './ReadinessBanner';
import FixCard from './FixCard';
import css from './ResumeReviewStep.module.scss';

interface ResumeReviewStepProps {
  review: ResumeReview | null;
  previousReadiness: Readiness | null;
  loading: boolean;
  error: 'expired' | 'unavailable' | null;
}

const Heading: React.FC = () => <h3>Resume feedback</h3>;

/**
 * The scrollable body of the review step: the readiness verdict, summary, and
 * prioritized fixes (or the loading / error / well-matched states). The action
 * buttons live in the drawer's sticky footer (owned by SearchDrawer), so they
 * stay reachable without scrolling past the full fix list.
 */
const ResumeReviewStep: React.FC<ResumeReviewStepProps> = ({
  review,
  previousReadiness,
  loading,
  error,
}) => {
  if (loading) {
    return (
      <div className={css.reviewStep}>
        <div className={css.loading}>
          <Spin size="large" />
          <p className={css.loadingText}>Reviewing your resume against this role…</p>
          <span className={css.loadingHint}>This can take up to 30 seconds.</span>
        </div>
      </div>
    );
  }

  if (error === 'expired') {
    return (
      <div className={css.reviewStep}>
        <Heading />
        <div className={css.errorState}>
          <p className={css.errorTitle}>Your upload session timed out</p>
          <p className={css.errorBody}>
            Please re-upload your resume to review it again.
          </p>
        </div>
      </div>
    );
  }

  if (error === 'unavailable') {
    return (
      <div className={css.reviewStep}>
        <Heading />
        <div className={css.errorState}>
          <p className={css.errorTitle}>We couldn’t review your resume right now</p>
          <p className={css.errorBody}>
            You can try again, or continue and create your search without the
            review.
          </p>
        </div>
      </div>
    );
  }

  if (!review) return null;

  const hasFixes = review.fixes.length > 0;
  const highCount = review.fixes.filter((f) => f.priority === 'high').length;

  return (
    <div className={css.reviewStep}>
      <Heading />
      <ReadinessBanner
        readiness={review.readiness}
        previousReadiness={previousReadiness}
      />

      {hasFixes && (
        <p className={css.optionalNote}>
          <BulbOutlined className={css.optionalIcon} aria-hidden="true" />
          <span>
            <strong>Optional</strong> — these are suggestions, not requirements. You
            can continue with your current resume whenever you're ready.
          </span>
        </p>
      )}

      <p className={css.summary}>{review.summary}</p>

      {hasFixes ? (
        <>
          <p className={css.count}>
            <span className={css.countTotal}>
              {review.fixes.length} optional suggestion
              {review.fixes.length === 1 ? '' : 's'}
            </span>
            {highCount > 0 && (
              <span className={css.countHigh}>{highCount} high priority</span>
            )}
          </p>
          <div className={css.fixes}>
            {review.fixes.map((fix, i) => (
              <FixCard key={i} fix={fix} />
            ))}
          </div>
        </>
      ) : (
        <div className={css.emptyState}>
          <CheckCircleOutlined className={css.emptyIcon} />
          <p>No critical fixes — your resume is well-matched to this role.</p>
        </div>
      )}
    </div>
  );
};

export default ResumeReviewStep;
